import { ChangeDetectorRef, Component, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core'
import * as fromAuth from '@app/users/store'
import { Store } from '@ngrx/store'
import { Observable, Subject } from 'rxjs'
import { takeUntil, tap } from 'rxjs/operators'
import * as fromStore from '@app/shared/store'
import * as moment from 'moment'
import { Router, ActivatedRoute, ParamMap } from '@angular/router'
import * as fromCoreStore from '@app/core/store'
import { getUser } from '@app/users/store/selectors/auth.selectors'
import { User } from '@app/shared/models'
import { MyCurrencyPipe } from '@app/shared/pipes/my-currency.pipe'
import { RedirectService } from '@app/shared'

@Component({
    selector: 'app-dashboard',
    template: `
    <div class="list__container">
        <div class="mr-auto ml-auto flex flex-col" style="width:98%;">
            <div class="flex items-center">
                <app-dashboard-filter style="flex: 1;"
                    [filters]="filters"
                    [currentUser]="currentUser"
                    [defaultDate]="defaultDate"
                    [loading]="loading"
                    (setFilter)="setFilter($event)"
                ></app-dashboard-filter>
                <div>
                    <button class="mt-3" [ngClass]="[this.currentUser?.company?.type == 'BROKER' ? 'mr-2' : 'mr-4']" (click)="toggleFullscreen()">                                    
                        <i style="font-size: 30px;"
                            [class.fa-expand]="!isFullscreen"
                            [class.fa-compress]="isFullscreen"
                            class="fa-solid text-gray-500"
                        ></i>
                    </button>
                </div>
            </div>

            @if(this.currentUser?.company?.type == 'CARRIER'){
                <app-dashboard-company
                    [currentUser]="currentUser"
                    [shop]="shop"
                    [fuelCost]="fuelCost"
                    [totalDriverPay]="totalDriverPay"
                    [companyExpense]="companyExpense"
                    [companyExpenseByCategory]="companyExpenseByCategory"
                    [claimsCounts]="claimsCounts"
                    [monthlyExpense]="monthlyExpense"
                    [claimsAcceptedAmount]="claimsAcceptedAmount"
                    [totalRevenue]="totalRevenue"
                    [top]="top"
                    [userIndex]="userIndex"
                    [profit]="profit"
                    [setLoading]="loading"
                    [filters]="filters"
                    [claimsAcceptedCounts]="claimsAcceptedCounts"
                ></app-dashboard-company>
            } @else if (this.currentUser?.company?.type == 'BROKER') {
                <app-dashboard-broker
                    [currentUser]="currentUser"
                    [data]="data"
                    [setLoading]="loading"
                    (onUpdate)="setFilter($event)"
                ></app-dashboard-broker>
            }
        </div>
    </div>
    `,
    styles: [
        `
            .list__container {
                display: flex;
                justify-content: center;
                width: 100%;
            }
        `
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class DashboardsComponent implements OnInit, OnDestroy {

    destroyed$ = new Subject<boolean>()
    currentUser: User
    filters
    shop
    fuelCost
    totalDriverPay
    companyExpense
    companyExpenseByCategory
    defaultDate
    claimsCounts
    monthlyExpense
    claimsAcceptedAmount
    urlParams = this.route.snapshot.queryParams
    totalRevenue
    top
    profit
    claimsAcceptedCounts
    userIndex = '0'
    loading = false
    data
    isFullscreen = false;

    constructor(private store: Store<fromStore.State>, private redirectService: RedirectService, private _MyCurrencyPipe: MyCurrencyPipe, private coreStore: Store<fromCoreStore.State>, private cdr: ChangeDetectorRef, private userStore: Store<fromAuth.State>, private router: Router, private route: ActivatedRoute) {
        this.defaultDate = {
            startDate: moment.utc().startOf('month').toISOString(),
            endDate: moment.utc().endOf('month').toISOString(),
            dataRange: 'thisMonth'
        }

        let urlParams = this.route.snapshot.queryParams
        this.store.dispatch(new fromStore.GetDashboardsFilters(urlParams))
        let currentUrl = window.location.href
        this.userIndex = String(parseInt(currentUrl.match(/\/(\d+)\//)?.[1] || '0', 10) || 0)

        // Seed the default date range into the URL only on a fresh landing (no query params).
        // Deferred with setTimeout so it runs AFTER the current route activation commits:
        // navigating during activation cancels the in-flight navigation and drops the
        // previous page from history (that's why Back jumped straight to Google).
        // replaceUrl keeps this from adding an extra history entry.
        if (!Object.keys(urlParams).length) {
            setTimeout(() => {
                this.router.navigate(
                    [`/${this.userIndex}/dashboards`],
                    {
                        queryParams: {
                            ...this.defaultDate
                        },
                        replaceUrl: true
                    }
                )
            })
        }

    }

    ngOnInit() {
        this.store.dispatch(new fromStore.GetDashboardsSuccess({}))
        this.store.select(getUser)
            .pipe(
                takeUntil(this.destroyed$),
                tap(currentUser => {
                    this.currentUser = currentUser
                })
            )
            .subscribe()

        document.addEventListener('fullscreenchange', () => {
            this.isFullscreen = !!document.fullscreenElement
            this.cdr.detectChanges()
        })

        this.store.select(fromStore.selectAllFiltersDashboards)
            .pipe(
                takeUntil(this.destroyed$),
                tap(filter => {

                    if ((filter?.dataRange == 'customDates' && this.filters?.dataRange && this.filters?.dataRange !== 'customDates') || (filter?.dataRange == 'customDates' && filter?.dataRange == this.filters?.dataRange)) {
                        return
                    }

                    this.loading = true

                    if (!filter?.startDate && filter?.startDate !== '') {
                        filter.startDate = this.defaultDate.startDate
                    }

                    if (!filter?.endDate && filter?.endDate !== '') {
                        filter.endDate = this.defaultDate.endDate
                    }

                    if (filter?.dataRange !== '' && !filter?.dataRange && filter?.dataRange !== 'default') {
                        filter.dataRange = this.defaultDate.dataRange
                    }

                    if (filter?.dataRange == 'default') {
                        delete filter.dataRange
                    }

                    for (let key in filter) {
                        if (filter[key] === '') {
                            delete filter[key]
                        }
                    }

                    this.filters = filter
                    this.store.dispatch(new fromStore.GetDashboards(filter))
                })
            )
            .subscribe()

        this.store.select(fromStore.selectDashboards)
            .pipe(
                takeUntil(this.destroyed$),
                tap((data: any) => {
                    if (data) {
                        if (Object.keys(data).length) {
                            this.loading = false

                            if (data?.type == 'BROKER') {
                                this.data = data
                            } else {
                                if (data?.top?.dispatchers?.length) {
                                    data.top.dispatchers.sort((a, b) => a.name.localeCompare(b.name))
                                }

                                this.fuelCost = this._MyCurrencyPipe.transform(data?.fuelCost)
                                this.shop = this._MyCurrencyPipe.transform(data?.shop)
                                this.totalDriverPay = this._MyCurrencyPipe.transform(data?.totalDriverPay)
                                this.companyExpense = this._MyCurrencyPipe.transform(data?.companyExpense)
                                this.companyExpenseByCategory = data?.companyExpenseByCategory || {}
                                this.claimsCounts = data?.claimsCounts
                                this.monthlyExpense = data?.expensesMonthly
                                this.claimsAcceptedAmount = this._MyCurrencyPipe.transform(data?.claimsAcceptedAmount)
                                this.totalRevenue = data?.totalRevenue
                                this.top = {
                                    dispatchers: data?.top?.dispatchers,
                                    customers: this.sortObs(data?.top?.customers || [], 'totalAmount'),
                                    drivers: this.sortObs(data?.top?.drivers || [], 'totalAmount')
                                }
                                this.profit = data?.profit
                                this.claimsAcceptedCounts = data?.claimsAcceptedCounts
                            }
                        }

                    }
                })
            )
            .subscribe()

    }

    sortObs(obs, prop) {
        obs.sort(function (a, b) {
            if (a[prop] < b[prop]) {
                return 1
            } else {
                return -1
            }
        })
        return obs
    }


    toggleFullscreen() {
        const elem = document.documentElement;

        if (!document.fullscreenElement) {
            elem.requestFullscreen();
        } else {
            document.exitFullscreen();
        }
    }

    setFilter(filter) {

        let currentFilter = { ...this.filters, ...filter }

        if (currentFilter.startDate == '') {
            currentFilter.startDate = this.defaultDate?.startDate
            currentFilter.dataRange = 'default'
        }

        if (currentFilter.endDate == '') {
            currentFilter.endDate = this.defaultDate?.endDate
            currentFilter.dataRange = 'default'
        }

        for (let key in currentFilter) {
            if (currentFilter[key] === null || currentFilter[key] === undefined) {
                delete currentFilter[key]
            }
        }

        let queryParams = { ...currentFilter }
        delete queryParams?.dataRange

        queryParams = {
            ...queryParams,
            dataRange: currentFilter?.dataRange
        }

        this.router.navigate(
            [`/${this.userIndex}/dashboards`],
            {
                queryParams,
                replaceUrl: true
            }
        )


        this.store.dispatch(new fromStore.GetDashboardsFilters(currentFilter))
    }


    ngOnDestroy(): void {
        this.destroyed$.next(true)
        this.destroyed$.complete()
    }
}
